import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { ErrorBox, StatusChip } from '../components/Layout';
import { QrCode } from '../components/QrCode';
import { laoDateTime, money } from '../lib/money';
import { STATUS_LABEL, type Payment } from '../types';

/** ສ້າງ QR ຮັບເງິນເອງ — ສຳລັບຮັບເງິນນອກລະບົບຄລີນິກ */
export function NewPaymentPage() {
  const [payment, setPayment] = useState<Payment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const amount = Number(String(form.get('amount') ?? '').replace(/[,\s]/g, ''));
    if (!Number.isInteger(amount) || amount <= 0) {
      setError('ຈຳນວນເງິນຕ້ອງເປັນເລກເຕັມກີບ');
      return;
    }

    const orderNo = String(form.get('orderNo') ?? '').trim();
    setBusy(true);
    setError(null);
    try {
      const { payment: created } = await api.createPayment({
        amount,
        description: String(form.get('description') ?? '').trim(),
        expiryMinutes: Number(form.get('expiryMinutes') ?? 30),
        ...(orderNo ? { orderNo } : {}),
      });
      setPayment(created);
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'ສ້າງ QR ບໍ່ສຳເລັດ');
    } finally {
      setBusy(false);
    }
  }

  if (payment) {
    const offset = Number(payment.amount) - Number(payment.requestedAmount);
    return (
      <>
        <div className="card detail-hero">
          <div className="detail-hero-title">
            <button type="button" className="detail-back" onClick={() => setPayment(null)}>
              ← ສ້າງ QR ໃໝ່
            </button>
            <h1>{money(payment.amount)}</h1>
          </div>
          <StatusChip status={payment.status} label={STATUS_LABEL[payment.status] ?? payment.status} />
        </div>

        <div className="detail-grid">
          <section className="card center detail-qr detail-accent detail-accent-pending">
            <QrCode value={payment.qrCode} size={200} />
            <p className="qr-amount">{money(payment.amount)}</p>
            <p className="hint">ສະແກນແລ້ວຍອດຂຶ້ນເອງ (BCEL One ແລະ ທະນາຄານອື່ນ)</p>
            <p className="muted">ໝົດອາຍຸ {laoDateTime(payment.expiresAt)}</p>
          </section>

          <section className="card">
            <dl className="summary">
              <dt>ລະຫັດ</dt>
              <dd>{payment.paymentId}</dd>
              <dt>ລາຍລະອຽດ</dt>
              <dd>{payment.description}</dd>
              {payment.orderNo && (
                <>
                  <dt>ເລກອ້າງອີງ</dt>
                  <dd>{payment.orderNo}</dd>
                </>
              )}
              <dt>ຍອດທີ່ຂໍ</dt>
              <dd>
                {money(payment.requestedAmount)}
                {offset > 0 && <span className="muted"> (+{offset} ກີບ ເພື່ອແຍກລາຍການ)</span>}
              </dd>
              <dt>ສ້າງເມື່ອ</dt>
              <dd>{laoDateTime(payment.createdAt)}</dd>
            </dl>

            <div className="actions">
              <Link to={`/payments/${payment.paymentId}`} className="pay">
                ໄປໜ້າລາຍລະອຽດ
              </Link>
            </div>
          </section>
        </div>

        <section>
          <h2 className="section-title">ປະຫວັດ Webhook</h2>
          <p className="muted">ຍັງບໍ່ມີການສົ່ງ</p>
        </section>
      </>
    );
  }

  return (
    <div className="page-fill center-page">
      <div className="narrow">
        <ErrorBox error={error} />
        <form className="card" onSubmit={submit}>
          <label>
            ຈຳນວນເງິນ (ກີບ)
            <input name="amount" inputMode="numeric" required placeholder="150000" />
          </label>
          <label>
            ລາຍລະອຽດ
            <input name="description" maxLength={255} required placeholder="ຄ່າກວດ" />
          </label>
          <label>
            ເລກອ້າງອີງ (ບໍ່ບັງຄັບ)
            <input name="orderNo" maxLength={50} />
          </label>
          <label>
            ໝົດອາຍຸໃນ (ນາທີ)
            <input name="expiryMinutes" type="number" min={1} max={1440} defaultValue={30} />
          </label>
          <p className="hint slip-hint">
            ຖ້າມີລາຍການຄ້າງທີ່ຍອດເທົ່າກັນ ລະບົບຈະບວກເສດ 1–99 ກີບ ເພື່ອແຍກວ່າເງິນທີ່ເຂົ້າມາເປັນຂອງລາຍການໃດ
          </p>
          <button type="submit" className="pay" disabled={busy}>
            {busy ? 'ກຳລັງສ້າງ…' : 'ສ້າງ QR'}
          </button>
        </form>
      </div>
    </div>
  );
}
